import { Component, OnDestroy, OnInit, ChangeDetectionStrategy, inject } from '@angular/core';
import { MatDialogRef, MatDialogContent, MatDialogActions, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Router } from '@angular/router';
import {
  CalendarDatePipe,
  CalendarEvent,
  CalendarMonthViewBeforeRenderEvent,
  CalendarMonthViewComponent,
  CalendarNextViewDirective,
  CalendarPreviousViewDirective,
  CalendarTodayDirective,
} from 'angular-calendar';
import { EventColor, DAYS_OF_WEEK } from 'calendar-utils';
import dayjs from 'dayjs';
import { Subject, Subscription } from 'rxjs';
import { debounceTime } from 'rxjs/operators';
import { EmployeeCalendarRegister } from 'src/app/models/employee_calendar_register.model';
import { Holiday } from 'src/app/models/holiday.model';
import {
  MonthVacationAvailabilityCompanyProduct,
  VacationDayRestriction,
  VacationPeriodRestrictionDetail,
} from 'src/app/models/month-vacation-availability.model';
import { User } from 'src/app/models/user.model';
import { WorkingTimeCategory } from 'src/app/models/working_time_category.model';
import { ApiService } from 'src/app/services/api.service';
import { PermissionsService } from 'src/app/services/permissions.service';
import { UserService } from 'src/app/services/user.service';
import { UtilsService } from 'src/app/services/utils.service';
import { BisualSettingService } from 'src/app/services/bisual-setting.service';
import { CdkScrollable } from '@angular/cdk/scrolling';
import { MatFormField, MatLabel } from '@angular/material/form-field';
import { MatSelect } from '@angular/material/select';
import { FormsModule } from '@angular/forms';
import { MatOption } from '@angular/material/autocomplete';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { MatButton } from '@angular/material/button';
import { DecimalPipe, TitleCasePipe } from '@angular/common';
import { MatDialog } from '@angular/material/dialog';
import { HtmlDialogComponent } from 'src/app/components/main/shared/html-dialog/html-dialog.component';
import { TextDialogComponent } from 'src/app/components/main/shared/text-dialog/text-dialog.component';
import { DomSanitizer } from '@angular/platform-browser';
import { MatTooltip } from '@angular/material/tooltip';
import { LongTask } from 'src/app/models/long_task.model';

export interface CreateVacationsDialogData {
  /** When true (Gestionar), admin can pick any worker and UI skips business restrictions. */
  manageForOthers?: boolean;
  userId?: number;
}

interface LocalVacationRequestPeriod {
  id: null;
  vacation_request_id: null;
  day: Date;
  created_at: null;
  updated_at: null;
  vacation_request: {
    user_id: number;
  };
}

@Component({
  selector: 'app-create-vacations-dialog',
  templateUrl: './create-vacations-dialog.component.html',
  styleUrls: ['./create-vacations-dialog.component.css'],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    CdkScrollable,
    MatDialogContent,
    MatFormField,
    MatLabel,
    MatSelect,
    FormsModule,
    MatOption,
    CalendarPreviousViewDirective,
    CalendarNextViewDirective,
    CalendarTodayDirective,
    CalendarDatePipe,
    CalendarMonthViewComponent,
    MatProgressSpinner,
    MatDialogActions,
    MatButton,
    DecimalPipe,
    TitleCasePipe,
    MatTooltip,
  ],
})
export class CreateVacationsDialogComponent implements OnInit, OnDestroy {
  dialogRef = inject<MatDialogRef<CreateVacationsDialogComponent>>(MatDialogRef);
  private api = inject(ApiService);
  protected utils = inject(UtilsService);
  protected router = inject(Router);
  private _user = inject(UserService);
  private bisualSetting = inject(BisualSettingService);
  private sanitizer = inject(DomSanitizer);
  private snack = inject(MatSnackBar);
  private dialog = inject(MatDialog);
  private permissions = inject(PermissionsService);
  data = inject<CreateVacationsDialogData | null>(MAT_DIALOG_DATA, { optional: true });

  // data
  year: number;
  years: number[];
  expected_hours_year: { [key: string]: { expected_hours: number } } | undefined;
  prevMonthDisabled: boolean = false;
  nextMonthDisabled: boolean = false;
  todayDisabled: boolean = false;
  is_intern: boolean = false;

  // res
  vacation_request_periods_to_ask: LocalVacationRequestPeriod[] = [];

  // coses de calendari
  viewDate: Date | undefined;
  user: User;
  workers: User[] = [];
  refresh = new Subject<void>();
  private monthChangeSubject = new Subject<void>();
  private monthChangeSubscription?: Subscription;
  private absencesSubscription?: Subscription;
  private monthAvailabilitySubscription?: Subscription;
  locale: string = 'es';
  weekStartsOn: number = DAYS_OF_WEEK.MONDAY;
  events: CalendarEvent[] = [];

  // spinner
  get spinner_prcnt(): number {
    if (this.is_intern) return 100;
    if (this.days_left == null || this.days_total == null || this.days_left == 0 || this.days_total == 0) return 0;
    else return (this.days_left / this.days_total) * 100;
  }
  days_left: number = null as any;
  days_total: number = null as any;

  /** Admin managing another worker from Gestionar — not used from Mi zona. */
  get isManageForOthers(): boolean {
    return this.permissions.isAdmin() && this.data?.manageForOthers === true;
  }

  // Helper method to get company_product spinner percentage
  // The spinner shows 0% when at minimum limit, and 100% when at full capacity
  // 0% of spinner = min_percentage (minimum allowed)
  // 100% of spinner = 100% (full capacity)
  getCompanyProductSpinnerPercentage(company_product: MonthVacationAvailabilityCompanyProduct): number {
    // If min_percentage >= 100, there's no margin above minimum
    if (company_product.min_percentage >= 100) {
      // If current is at or above 100%, show 100% (we're at full capacity)
      // If current is below 100%, show 0% (we're below the minimum which is >= 100%)
      return company_product.current_percentage >= 100 ? 100 : 0;
    }

    // Calculate the usable range: from min_percentage to 100%
    const usableRange = 100 - company_product.min_percentage;

    // Calculate how much we're above the minimum
    const aboveMinimum = company_product.current_percentage - company_product.min_percentage;

    // Convert to spinner percentage: 0% = at minimum, 100% = at full capacity
    const spinnerPercentage = usableRange > 0 ? (aboveMinimum / usableRange) * 100 : 0;

    // Clamp between 0 and 100
    return Math.max(0, Math.min(100, spinnerPercentage));
  }

  // Helper method to get tooltip text for company_product percentage
  getCompanyProductTooltip(company_product: MonthVacationAvailabilityCompanyProduct): string {
    const current = company_product.current_percentage.toFixed(1);
    const min = company_product.min_percentage.toFixed(1);
    return `${current}% de ${min}% de límite mínimo`;
  }

  // Helper method to get restriction message from restriction types
  getRestrictionMessage(restrictions: VacationDayRestriction[]): string {
    const advanceNoticeDays = this.bisualSetting.get('vacation-advance-notice-days')?.value || '30';
    const longPeriodSeparationDays = this.bisualSetting.get('vacation-long-period-separation-days')?.value || '30';
    const shortPeriodWeeksGap = this.bisualSetting.get('vacation-short-period-weeks-gap')?.value || '2';
    const shortPeriodSeparationDays = this.bisualSetting.get('vacation-short-period-separation-days')?.value || '15';
    const messages: { [key: string]: string } = {
      no_working_contract: 'No tienes un contrato de trabajo activo para este día.',
      not_working_day: 'Este día no es laborable (fin de semana, festivo o sin horas esperadas).',
      advance_notice: `Para periodos largos de vacaciones, debes solicitarlos con al menos ${advanceNoticeDays} días de antelación.`,
      short_period_events: `No puedes solicitar vacaciones cuando hay un evento bloqueado para reuniones programado en ese día.`,
      past_date: 'No puedes solicitar vacaciones anteriores a hace 1 mes.',
      year_attribution: 'Este día no pertenece al año de vacaciones seleccionado.',
      existing_vacation: 'Ya tienes vacaciones solicitadas para este día.',
      capacity_limit: 'No puedes solicitar vacaciones este día porque se superaría el límite mínimo de capacidad del producto.',
      annual_limit: 'Este día excedería tu límite anual de días de vacaciones.',
      long_period_limit: `Este día crearía dos periodos largos de vacaciones dentro de ${longPeriodSeparationDays} días laborables.`,
      short_long_period_separation: `No puedes tener un periodo corto y un periodo largo de vacaciones a menos de ${longPeriodSeparationDays} días de separación.`,
      long_period_next_day_only:
        'Cuando tienes un periodo largo de vacaciones, solo puedes seleccionar el día inmediatamente después laborable. Debes seleccionar los días en orden consecutivo.',
      short_period_limit: `Este día crearía dos periodos cortos de vacaciones dentro de ${shortPeriodWeeksGap} semanas o ${shortPeriodSeparationDays} días.`,
      pm_techlead_limit: 'Este día violaría la restricción de Product Manager y Tech Lead no pueden estar fuera simultáneamente.',
      team_capacity_limit: 'Este día reduciría la capacidad del equipo por debajo del mínimo permitido en algún producto.',
    };

    for (const restriction of restrictions) {
      if (typeof restriction !== 'string' && restriction.code === 'short_period_limit') {
        return this.buildShortPeriodLimitMessage(restriction, shortPeriodWeeksGap, shortPeriodSeparationDays);
      }

      const code = typeof restriction === 'string' ? restriction : restriction.code;
      if (messages[code]) {
        return messages[code];
      }
    }

    return 'Este día no está disponible para solicitar vacaciones.';
  }

  // productos y capacidad
  company_products: MonthVacationAvailabilityCompanyProduct[] = [];
  monthAvailabilityLoading: boolean = false;
  restrictedDays: { [date: string]: VacationDayRestriction[] } = {};

  // gestió de sol·licitud
  is_sending_request: boolean = false;

  get reservarButtonDisabled(): boolean {
    return this.vacation_request_periods_to_ask.length == 0 || this.is_sending_request;
  }

  constructor() {
    // Subscribe to month changes with debounce
    this.monthChangeSubscription = this.monthChangeSubject.pipe(debounceTime(1000)).subscribe(() => {
      // Cancel any ongoing requests
      this.cancelOngoingRequests();
      // Fetch new data
      this.fetchCalendarData();
      this.fetchMonthAvailability();
    });
  }

  ngOnInit(): void {
    this.getUser();
    if (this.isManageForOthers) {
      this.loadWorkers();
    }
    this.initYearsDropdown();
    this.onYearSelected(new Date().getFullYear());
    this.setIsIntern();
  }

  ngOnDestroy(): void {
    // Cancel all ongoing requests
    this.cancelOngoingRequests();
    // Clean up month change subscription
    this.monthChangeSubscription?.unsubscribe();
  }

  private cancelOngoingRequests(): void {
    if (this.absencesSubscription) {
      this.absencesSubscription.unsubscribe();
      this.absencesSubscription = undefined;
    }
    if (this.monthAvailabilitySubscription) {
      this.monthAvailabilitySubscription.unsubscribe();
      this.monthAvailabilitySubscription = undefined;
    }
  }

  viewPolicy() {
    this.api.getWikiNodeBySlug('rrhh').subscribe({
      next: node => {
        this.router.navigate(['/employees', 'wiki', node.id, 'article', 'vacation-policy'], { queryParams: { upper_node: node.id } });
        this.dialogRef.close();
      },
      error: () => {
        this.snack.open('No se pudo obtener el nodo de la wiki', 'Ok', { duration: 3000 });
      },
    });
  }

  showRequestedDays() {
    const daysList = this.vacation_request_periods_to_ask.map(vrp => dayjs(vrp.day).format('DD/MM/YYYY')).join('<br>');

    this.dialog.open(HtmlDialogComponent, {
      width: '400px',
      data: {
        title: 'Días solicitados',
        html: this.sanitizer.bypassSecurityTrustHtml(daysList || 'No hay días solicitados'),
        dialog_close_button: 'Cerrar',
      },
    });
  }

  dayClicked({ date, events }: { date: Date; events: CalendarEvent[] }): void {
    // Disable clicks while loading data
    if (this.monthAvailabilityLoading || this.is_sending_request) {
      return;
    }

    const mom_date: dayjs.Dayjs = dayjs(date);
    // Use 'YYYY-MM-DD' format to match backend (which uses 'Y-m-d' = 'YYYY-MM-DD')
    const dateKey = mom_date.format('YYYY-MM-DD');

    const events_vacation_request_local = events.filter(e => e.meta.type === 'vacation_request_local');

    // If day is already selected, allow removing it even if restricted
    if (events_vacation_request_local.length > 0) {
      // Remove the day from events
      this.events = this.events.filter(e => {
        return e.meta.type !== 'vacation_request_local' || !dayjs(e.start).isSame(mom_date, 'day');
      });

      // Remove the day from the array
      const index = this.vacation_request_periods_to_ask.findIndex(vrp => dayjs(vrp.day).format('YYYY-MM-DD') === dateKey);
      if (index !== -1) {
        this.vacation_request_periods_to_ask.splice(index, 1);
        this.days_left++; // Restore the day
        this.refresh.next();
        // Recalculate restrictions after removing
        this.fetchMonthAvailability();
      }
      return;
    }

    // Check if day is restricted by backend (only if not already selected)
    // Gestionar (admin for others) skips UI restriction blocks; Mi zona keeps them.
    if (!this.isManageForOthers && this.restrictedDays[dateKey] && this.restrictedDays[dateKey].length > 0) {
      const restrictions = this.restrictedDays[dateKey];
      const message = this.getRestrictionMessage(restrictions);
      this.snack.open(message, 'Ok', { duration: 8000 });
      return;
    }

    // UI validation: check if user is trying to select a day from a different month
    if (!dayjs(this.viewDate).isSame(mom_date, 'month')) {
      this.snack.open("Si quieres reservar para este mes, cambia con 'Siguiente' o 'Anterior'.", 'Ok', { duration: 3000 });
      return;
    }

    if (events_vacation_request_local.length == 0) {
      // creem
      // Check days_left (UI validation - backend also validates this but we check here for better UX)
      if (!this.isManageForOthers && this.days_left < 1) {
        this.snack.open('No puedes solicitar más días.', 'Ok', { duration: 3000 });
        return;
      }

      const new_vacation_request_period = this.createVacationRequestLocalFromDate(mom_date);
      this.vacation_request_periods_to_ask.push(new_vacation_request_period);
      this.events.push(this.vacationRequestPeriodToEvent(new_vacation_request_period, true));
      this.days_left--;
    } else {
      // Eliminem
      this.events = this.events.filter(e => {
        return e.meta.type !== 'vacation_request_local' || !dayjs(e.start).isSame(mom_date, 'day');
      });
      let daysDeleted: number = 0;
      this.vacation_request_periods_to_ask = this.vacation_request_periods_to_ask.filter(vrp => {
        const res = !dayjs(vrp.day).isSame(date, 'day');
        if (!res) daysDeleted++;
        return res;
      });
      this.days_left += daysDeleted;
    }

    // Recalculate month availability with updated selected days
    this.fetchMonthAvailability();
    this.refresh.next();
  }

  handleEvent(action: string, event: CalendarEvent): void {
    // this.modalData = { event, action };
    // this.modal.open(this.modalContent, { size: 'lg' });
  }

  beforeMonthViewRender(renderEvent: CalendarMonthViewBeforeRenderEvent): void {
    renderEvent.body.forEach(day => {
      const date = day.date;
      // Use 'YYYY-MM-DD' format to match backend (which uses 'Y-m-d' = 'YYYY-MM-DD')
      const dateKey = dayjs(date).format('YYYY-MM-DD');
      // Only trust backend restrictions - it handles all validations (not_working_day, past_date, existing_vacation, holidays, etc.)
      const isRestricted = !this.isManageForOthers && this.restrictedDays[dateKey] && this.restrictedDays[dateKey].length > 0;

      if (isRestricted) {
        day.cssClass += ' bg-not-working-day';
      }
    });
  }

  onTargetUserSelected(userId: number): void {
    const selected = this.workers.find(worker => worker.id === userId);
    if (selected == null) {
      return;
    }
    this.user = selected;
    this.vacation_request_periods_to_ask = [];
    this.fetchWorkingContractsForYear();
    this.fetchCurrentYearStatus();
    this.onChangeMonth();
  }

  onChangeMonth() {
    this.prevMonthDisabled = !dayjs(this.viewDate).subtract(1, 'month').isSame(dayjs().set('year', this.year), 'year');
    this.nextMonthDisabled =
      !(
        (
          dayjs(this.viewDate).add(1, 'month').isSame(dayjs().set('year', this.year), 'year') || // mateix any
          (dayjs(this.viewDate)
            .add(1, 'month')
            .isSame(dayjs().set('year', this.year + 1), 'year') &&
            dayjs(this.viewDate)
              .add(1, 'month')
              .isSame(
                dayjs()
                  .set('year', this.year + 1)
                  .set('month', 0),
                'month'
              ))
        ) // any següent, gener
      ) ||
      (this.is_intern &&
        dayjs(this.viewDate).startOf('month').isSame(dayjs(this.user.current_working_contract?.end_date).startOf('month')));
    // Emit to subject for debounced fetching
    this.monthChangeSubject.next();
  }

  onYearSelected(year: number) {
    this.vacation_request_periods_to_ask = [];
    this.year = year;
    this.todayDisabled = dayjs().year() !== this.year;
    this.viewDate = dayjs()
      .set('year', this.year)
      .set('month', this.year == dayjs().year() ? dayjs().month() : 0)
      .toDate();
    this.fetchWorkingContractsForYear();
    this.fetchCurrentYearStatus();
    this.onChangeMonth(); // això ja fa fetchData i fetchMonthAvailability
  }

  reservar() {
    if (this.is_sending_request) {
      return; // Prevent multiple clicks
    }

    const days = this.vacation_request_periods_to_ask.map(vrp => dayjs(vrp.day).format('YYYY-MM-DD'));

    this.is_sending_request = true;
    const targetUserId = this.isManageForOthers ? this.user.id : undefined;
    this.api.createVacations(days, this.year, targetUserId).subscribe({
      next: (_long_task: LongTask) => {
        this.is_sending_request = false;
        this.dialog.open(TextDialogComponent, {
          data: {
            title: 'Solicitud enviada',
            body: 'Estamos procesando tu solicitud de vacaciones. En unos momentos recibirás una notificación con el resultado.',
            dialog_close_button: 'Entendido',
          },
        });
        this.dialogRef.close({ refresh: true });
      },
      error: error => {
        this.is_sending_request = false;
        console.error('Error creating vacations:', error);
        this.snack.open(error?.error?.message || 'No se pudo enviar la solicitud de vacaciones', 'Ok', {
          duration: 5000,
        });
      },
    });
  }

  private getUser() {
    const currentUser = this._user.getCurrentUser();
    if (this.isManageForOthers && this.data?.userId != null) {
      this.user = { ...currentUser, id: this.data.userId } as User;
      this.api.getUser(String(this.data.userId)).subscribe({
        next: user => {
          this.user = user;
          this.fetchWorkingContractsForYear();
          this.fetchCurrentYearStatus();
        },
      });
    } else {
      this.user = currentUser;
    }
  }

  private loadWorkers(): void {
    this.api
      .getUsers({
        scopes: 'activeWorkers',
        order_by: 'name:asc',
        per_page: 500,
      })
      .subscribe({
        next: response => {
          this.workers = response.data ?? [];
          const selected = this.workers.find(worker => worker.id === this.user?.id);
          if (selected != null) {
            this.user = selected;
          }
        },
      });
  }

  private fetchCalendarData() {
    // Clear events before fetching new data
    this.events = [];
    this.fetchAbsences();
  }

  private fetchWorkingContractsForYear() {
    this.api.getWorkingContractVariationsForYear(this.user.id, this.year).subscribe(data => {
      this.expected_hours_year = data;
    });
  }

  private fetchAbsences() {
    // Cancel previous request if any
    if (this.absencesSubscription) {
      this.absencesSubscription.unsubscribe();
    }

    this.is_sending_request = true;
    const selected_date: dayjs.Dayjs = dayjs(this.viewDate);
    this.absencesSubscription = this.api
      .getAllEmployeeAbsencesThatAffectMe(
        selected_date.clone().startOf('month').startOf('week').toDate(),
        selected_date.clone().endOf('month').endOf('week').toDate()
      )
      .subscribe(
        data => {
          // Processem vacation requests pending (ya no existe, se eliminó VacationRequest)
          // Las vacaciones ahora vienen en employee_calendar_registers

          // Processem Holidays
          if (data.my_holidays && Array.isArray(data.my_holidays)) {
            data.my_holidays.forEach(h => {
              this.events.push(this.holidayToEvent(h));
            });
          }

          // Processem vacances
          if (data.employee_calendar_registers && Array.isArray(data.employee_calendar_registers)) {
            data.employee_calendar_registers
              .filter(ecr => ecr.all_day)
              .forEach(ecr => {
                // suposem que tots els que ens arriben aqui son amb is_working_time = false
                this.events.push(this.ecrToEvent(ecr));
              });
          }

          // Processem vacation requests que tenim ara
          this.vacation_request_periods_to_ask.forEach(vrp => {
            this.events.push(this.vacationRequestPeriodToEvent(vrp, true));
          });

          this.refresh.next();
          this.is_sending_request = false;
          this.absencesSubscription = undefined;
        },
        () => {
          this.is_sending_request = false;
          this.absencesSubscription = undefined;
        }
      );
  }

  private vacationRequestPeriodToEvent(vrp: LocalVacationRequestPeriod, local_only: boolean = false): CalendarEvent<any> {
    return {
      start: dayjs(vrp.day).startOf('day').toDate(),
      end: dayjs(vrp.day).endOf('day').toDate(),
      title: 'Petición de vacaciones pendiente',
      color: local_only ? colors.vacation_requests_local : colors.vacation_requests,
      actions: undefined,
      allDay: true,
      resizable: {
        beforeStart: false,
        afterEnd: false,
      },
      draggable: false,
      meta: { type: local_only ? 'vacation_request_local' : 'vacation_request', user_id: vrp.vacation_request.user_id },
    };
  }

  private holidayToEvent(h: Holiday): CalendarEvent<any> {
    return {
      start: dayjs(h.date).startOf('day').toDate(),
      end: dayjs(h.date).endOf('day').toDate(),
      title: h.name,
      color: colors.holiday,
      actions: undefined,
      allDay: true,
      resizable: {
        beforeStart: false,
        afterEnd: false,
      },
      draggable: false,
      meta: { type: 'holiday' },
    };
  }

  private ecrToEvent(ecr: EmployeeCalendarRegister) {
    const cat = ecr.working_time_category as WorkingTimeCategory;
    const event_name = cat.name === 'Vacaciones' || cat.name === 'Festivo local' ? cat.name : 'Ausencia';
    return {
      start: new Date(ecr.from_date),
      end: ecr.all_day ? dayjs(ecr.from_date).endOf('day').toDate() : ecr.to_date,
      title: `${event_name} (${ecr.user!.name + ' ' + ecr.user!.surnames})`,
      color: cat.color != null ? { primary: cat.color, secondary: colors.vacation.secondary } : colors.vacation,
      actions: undefined,
      allDay: ecr.all_day,
      resizable: {
        beforeStart: false,
        afterEnd: false,
      },
      draggable: false,
      meta: { type: 'ecr', user_id: ecr.user_id },
    };
  }

  private createVacationRequestLocalFromDate(mom: dayjs.Dayjs): LocalVacationRequestPeriod {
    return {
      id: null,
      vacation_request_id: null,
      day: mom.toDate(),
      created_at: null,
      updated_at: null,
      vacation_request: {
        user_id: this.user.id,
      },
    };
  }

  private initYearsDropdown() {
    this.years = [dayjs().year(), dayjs().add(1, 'year').year()];

    const start_year = dayjs(this.user.current_working_contract?.start_date).year();
    const end_year = dayjs(this.user.current_working_contract?.end_date).year();
    this.years = this.years.filter(year => year >= start_year && (end_year ? year <= end_year : true));
  }

  private fetchCurrentYearStatus() {
    this.api.getUserVacations(this.user.id, this.year).subscribe(data => {
      this.days_total = data.spent.days + data.pending.days + data.left.days;
      this.days_left = data.left.days;
    });
  }

  private fetchMonthAvailability() {
    if (!this.viewDate) return;

    // Cancel previous request if any
    if (this.monthAvailabilitySubscription) {
      this.monthAvailabilitySubscription.unsubscribe();
    }

    this.monthAvailabilityLoading = true;
    const month = dayjs(this.viewDate).format('YYYY-MM');
    // Get ALL selected days (not just current month) so backend can calculate restrictions correctly
    // This is important for period restrictions that span across months (e.g., long periods)
    const selectedDays = this.vacation_request_periods_to_ask.map(vrp => dayjs(vrp.day).format('YYYY-MM-DD'));

    this.monthAvailabilitySubscription = this.api.getMonthAvailability(month, selectedDays, this.year).subscribe({
      next: data => {
        this.company_products = data.company_products || [];
        this.restrictedDays = data.restricted_days || {};
        this.monthAvailabilityLoading = false;
        this.monthAvailabilitySubscription = undefined;
        // Refresh calendar to show restricted days
        this.refresh.next();
      },
      error: () => {
        this.monthAvailabilityLoading = false;
        this.monthAvailabilitySubscription = undefined;
      },
    });
  }

  private setIsIntern(): void {
    this.is_intern = this.user.current_working_contract?.working_contract_type?.slug === 'practice';
  }

  private buildShortPeriodLimitMessage(
    restriction: VacationPeriodRestrictionDetail,
    short_period_weeks_gap: string,
    short_period_separation_days: string
  ): string {
    const blocking_label = this.formatVacationPeriodLabel(restriction.blocking_period);
    const requested_label = this.formatVacationPeriodLabel(restriction.requested_period);
    const minimum_days = restriction.minimum_separation_days ?? short_period_separation_days;
    let message = `Este día crearía dos periodos cortos de vacaciones dentro de ${short_period_weeks_gap} semanas o ${minimum_days} días. Periodo que bloquea: ${blocking_label}. Periodo solicitado: ${requested_label}.`;

    if (restriction.counted_separation_days != null && restriction.counted_from && restriction.counted_to) {
      message += ` Separación contada: del ${restriction.counted_from} al ${restriction.counted_to} (${restriction.counted_separation_days} días; mínimo ${minimum_days}).`;
    } else if (restriction.counted_separation_days != null) {
      message += ` Separación contada: ${restriction.counted_separation_days} días; mínimo ${minimum_days}.`;
    }

    return message;
  }

  private formatVacationPeriodLabel(period: { start: string; end: string }): string {
    if (period.start === period.end) {
      return period.start;
    }

    return `${period.start} a ${period.end}`;
  }
}

const colors: Record<string, EventColor> = {
  vacation: {
    primary: '#ce2525',
    secondary: null as any,
  },
  holiday: {
    primary: '#E3B163',
    secondary: null as any,
  },
  vacation_requests: {
    primary: '#6BC434',
    secondary: null as any,
  },
  vacation_requests_local: {
    primary: '#0C83E1',
    secondary: null as any,
  },
};
